import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import HomepageFooter from "@/components/homepage/HomepageFooter";
import HomepageLeadModal from "@/components/homepage/HomepageLeadModal";
import HomepageStickyCta from "@/components/homepage/HomepageStickyCta";
import Navbar from "@/components/homepage/Navbar";
import HtmlBody from "@/components/cms/HtmlBody";
import { absoluteMediaUrl, fetchDispatchBySlug, type Dispatch } from "@/lib/cms";

const DATE_FMT = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "long",
  year: "numeric",
});

function formatLongDate(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return DATE_FMT.format(d);
}

type LoadState =
  | { status: "loading" }
  | { status: "ready"; dispatch: Dispatch }
  | { status: "not-found" }
  | { status: "error"; message: string };

export default function DailyAIUpdateDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const safeSlug = slug?.trim() ?? "";

  const [state, setState] = useState<LoadState>({ status: "loading" });

  const [leadModal, setLeadModal] = useState<{ open: boolean; email: string }>({
    open: false,
    email: "",
  });
  const openLeadModal = (email: string) => setLeadModal({ open: true, email });
  const closeLeadModal = () => setLeadModal({ open: false, email: "" });

  useEffect(() => {
    if (!safeSlug) {
      setState({ status: "not-found" });
      return;
    }
    let cancelled = false;
    fetchDispatchBySlug(safeSlug)
      .then((dispatch) => {
        if (cancelled) return;
        if (!dispatch) setState({ status: "not-found" });
        else setState({ status: "ready", dispatch });
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[DailyAIUpdateDetailPage] fetch error", err);
        setState({
          status: "error",
          message: "Could not load this dispatch right now.",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [safeSlug]);

  return (
    <main
      className="flex min-h-svh w-full flex-col bg-black pb-24 text-white max-md:pb-[calc(5rem+env(safe-area-inset-bottom,0px))]"
      style={{ fontFamily: "var(--font-fira)" }}
    >
      <Navbar />

      <article
        id="daily-ai-update-hero"
        className="mx-auto w-[90%] max-w-3xl md:w-[80%]"
      >
        <Link
          to="/daily-ai-updates"
          className="mt-6 inline-flex items-center gap-1.5 text-[13px] text-white/55 transition hover:text-white md:mt-8 md:text-sm"
        >
          <span aria-hidden>←</span> Back to archive
        </Link>

        {state.status === "loading" ? (
          <p className="mt-16 text-center text-sm text-white/55">
            Loading dispatch…
          </p>
        ) : state.status === "not-found" ? (
          <div className="mt-16 text-center">
            <h1 className="font-fira-condensed text-[28px] font-medium text-white md:text-[36px]">
              Dispatch not found
            </h1>
            <p className="mt-3 text-sm text-white/65">
              The dispatch you’re looking for may have been removed or the link
              is incorrect.
            </p>
          </div>
        ) : state.status === "error" ? (
          <div className="mt-16 text-center">
            <p className="text-sm text-red-400">{state.message}</p>
          </div>
        ) : (
          <DispatchContent dispatch={state.dispatch} />
        )}
      </article>

      <HomepageFooter
        className="mt-12 md:mt-16"
        onEmailContinue={openLeadModal}
      />
      <HomepageStickyCta
        heroSectionId="daily-ai-update-hero"
        ctaSectionId="homepage-cta"
        marketingBasePath="/daily-ai-updates"
        activePathPrefixes={["/daily-ai-updates"]}
        onEmailContinue={openLeadModal}
      />
      <HomepageLeadModal
        open={leadModal.open}
        initialEmail={leadModal.email}
        onClose={closeLeadModal}
      />
    </main>
  );
}

function DispatchContent({ dispatch }: { dispatch: Dispatch }) {
  const type = (dispatch.type ?? "").trim();
  const coverImage =
    typeof dispatch.coverImage === "object" && dispatch.coverImage !== null
      ? dispatch.coverImage
      : null;
  const coverUrl = absoluteMediaUrl(coverImage?.url ?? undefined);

  return (
    <>
      <header className="font-fira mt-8 md:mt-10">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-white/65 md:text-sm">
          {type ? (
            <span className="rounded-md border border-white/15 bg-black/40 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-white">
              {type}
            </span>
          ) : null}
          <time dateTime={dispatch.publishedDate} className="tabular-nums">
            {formatLongDate(dispatch.publishedDate)}
          </time>
          {dispatch.duration ? (
            <>
              <span className="text-white/35" aria-hidden>
                ·
              </span>
              <span>{dispatch.duration}</span>
            </>
          ) : null}
        </div>

        <h1 className="mt-5 text-[28px] font-medium leading-tight tracking-tight text-white md:text-[36px]">
          {dispatch.title}
        </h1>

        {dispatch.excerpt ? (
          <p className="mt-3 leading-relaxed text-white/85 md:text-base">
            {dispatch.excerpt}
          </p>
        ) : null}
      </header>

      {coverUrl ? (
        <div className="mt-10 overflow-hidden rounded-2xl border border-[#FFFFFF52]">
          <img
            src={coverUrl}
            alt={coverImage?.alt ?? ""}
            className="aspect-[16/10] w-full object-cover md:aspect-[2/1]"
            loading="eager"
            decoding="async"
          />
        </div>
      ) : null}

      <div className={`relative z-10 w-[90%] mx-auto md:w-full ${coverUrl ? "-mt-8 rounded-t-2xl bg-black pt-6 md:mt-10 md:rounded-none md:bg-transparent md:pt-0" : "mt-10"}`}>
        <HtmlBody html={dispatch.body} />
      </div>
    </>
  );
}
